import {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import FileManager, {
  FileManagerProps,
} from "domains/file-manager/components/FileManager";
import Search from "domains/file-manager/components/Search";
import { useFileModelHandler } from "domains/file-manager/hooks/useFileModelHandler";
import { FileModelType } from "domains/file-manager/interfaces";
import ModelSelectTags from "domains/models/components/ModelSelectTags";
import { BLEND_CATEGORIES } from "domains/models/constants/blend";
import { ModelTag } from "domains/models/constants/tags";
import { useCreateBlendContext } from "domains/models/contexts/CreateBlendProvider";
import useAllModels, {
  UseAllModelsArgs,
} from "domains/models/hooks/useAllModels";
import useRouter from "domains/navigation/hooks/useRouter";
import {
  Tab,
  TabIndicator,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "domains/ui/components/Tabs";
import { createTabs, useTabs } from "domains/ui/hooks/useTabs";

import { Box, Heading, HStack, Text, VStack } from "@chakra-ui/react";

import BlendCategoriesList from "./BlendCategoriesList";

export const TABS = createTabs({
  ids: ["PUBLIC", "PRIVATE"],
  items: {
    PUBLIC: "public",
    PRIVATE: "private",
  },
});

interface BlendAddConceptsTabsProps {
  contentScrollRef: RefObject<HTMLDivElement>;
}

export default function BlendAddConceptTabs({
  contentScrollRef,
}: BlendAddConceptsTabsProps) {
  const router = useRouter();
  const { type, search, concepts, addConcept, removeConcept, setSearch } =
    useCreateBlendContext();
  const [selectedTags, setSelectedTags] = useState<ModelTag[]>([]);
  const { tabIndex, onTabChange } = useTabs(TABS);

  const conceptIdsWithoutAdd = useMemo(
    () => concepts.map((concept) => concept.modelId),
    [concepts]
  );

  const useAllModelsArgs: UseAllModelsArgs = useMemo(
    () => ({
      typesFilter: [type === "sd-xl" ? "sd-xl-lora" : "flux.1-lora"],
      searchTerm: search,
      statusesFilter: ["trained"],
      tagsFilter: selectedTags,
    }),
    [type, search, selectedTags]
  );

  const publicConcepts = useAllModels({
    privacyFilter: "public",
    ...useAllModelsArgs,
  });
  const privateConcepts = useAllModels({
    privacyFilter: "private",
    ...useAllModelsArgs,
  });

  // ----------------------------------

  const handleAdd = useCallback(
    (file: FileModelType) => {
      addConcept(file.meta.id);
    },
    [addConcept]
  );

  const handleUndoAdd = useCallback(
    (file: FileModelType) => removeConcept(file.meta.id),
    [removeConcept]
  );

  const handleOpen = useCallback(
    (file: FileModelType) => {
      void router.push({
        pathname: router.pathname,
        query: {
          ...router.query,
          selectedModelId: file.id,
        },
      });
    },
    [router]
  );

  // ----------------------------------

  const modelFileHandler = useFileModelHandler({
    conceptIdsWithoutAdd,
    onAdd: handleAdd,
    onUndoAdd: handleUndoAdd,
    withLink: false,
  });

  const fileManagerProps: {
    fileHandlers: FileManagerProps["fileHandlers"];
    options: FileManagerProps["options"];
    scrollRef: RefObject<HTMLDivElement>;
  } = useMemo(
    () => ({
      fileHandlers: {
        model: {
          ...modelFileHandler,
          onOpen: handleOpen,
          EmptyState: (
            <Heading size="md">
              {search === "" ? "" : "No models found."}
            </Heading>
          ),
        },
      },
      options: {
        canSelect: false,
        canChangeView: false,
        canChangeNumberOfColumns: false,
        showFileNames: "always",
        numberOfColumns: 6,
        withoutTopBar: true,
      },
      scrollRef: contentScrollRef,
    }),
    [contentScrollRef, modelFileHandler, handleOpen, search]
  );

  // ----------------------------------

  if (tabIndex === undefined) {
    return null;
  }

  return (
    <Tabs isLazy index={tabIndex} onChange={onTabChange} w="100%">
      <TabList>
        <Tab>Platform Models</Tab>
        <Tab>Your Models</Tab>
      </TabList>
      <TabIndicator key={`tab-indicator-${tabIndex}`} />

      <TabPanels>
        <TabPanel w="full">
          <TabHeader
            selectedTags={selectedTags}
            setSelectedTags={setSelectedTags}
            setSearch={setSearch}
          />

          <VStack align="stretch" spacing={4}>
            <BlendCategoriesList categories={BLEND_CATEGORIES} />

            <VStack align="stretch" spacing={2}>
              <Text color="textSecondary" textAlign="left" size="body.bold.md">
                Trending
              </Text>
              <FileManager {...publicConcepts} {...fileManagerProps} />
            </VStack>
          </VStack>
        </TabPanel>

        <TabPanel>
          <TabHeader
            selectedTags={selectedTags}
            setSelectedTags={setSelectedTags}
            setSearch={setSearch}
          />
          <FileManager {...privateConcepts} {...fileManagerProps} />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
}

// ------------------------------------

interface TabHeaderProps {
  selectedTags: ModelTag[];
  setSelectedTags: Dispatch<SetStateAction<ModelTag[]>>;
  setSearch: (search: string) => void;
}

function TabHeader({
  selectedTags,
  setSelectedTags,
  setSearch,
}: TabHeaderProps) {
  return (
    <HStack w="100%" pt={2} pb={6} spacing={2}>
      <Box w="350px">
        <Search onSearch={setSearch} placeholder="Search Models" />
      </Box>
      <ModelSelectTags
        selectedTags={selectedTags}
        setSelectedTags={setSelectedTags}
      />
    </HStack>
  );
}
